/**
 * Wire format for files written to Drive. MUST stay bit-compatible with the
 * Kotlin DTOs in app/src/main/java/com/shell/sync/SyncEngine.kt
 * (SyncedStorage and SyncedModule) — a single Google account syncs both
 * Android and web identically.
 */

export const SCHEMA_VERSION = 1;
export const REMOTE_STORAGE_PREFIX = 'shell/storage';
export const REMOTE_MODULES_PREFIX = 'shell/modules';
export const REMOTE_ROOT_PREFIX = 'shell/';
/** Rolling point-in-time backups. The reconciler IGNORES this namespace — it
 *  is an additive history layer, never read during sync. */
export const REMOTE_CHECKPOINTS_PREFIX = 'shell/checkpoints';

export interface StorageEntry {
  value: string;
  ts: number;
}

export interface SyncedStorage {
  schemaVersion: number;
  moduleId: string;
  lastModifiedMs: number;
  deviceId: string;
  keys: Record<string, StorageEntry>;
}

export interface SyncedModule {
  schemaVersion: number;
  moduleId: string;
  lastModifiedMs: number;
  deviceId: string;
  files: Record<string, string>;
  /**
   * Tombstone marker. A real module deletion WRITES this file with
   * deleted=true (files={}) instead of removing it — that's the only signal
   * that propagates deletion to other devices. A MISSING file just means
   * "not on Drive" (e.g. sync was turned off + cleaned up) and must never
   * delete anything locally.
   */
  deleted?: boolean;
  deletedAtMs?: number;
}

/** File metadata returned by a SyncProvider's listFiles. */
export interface RemoteFile {
  path: string;
  remoteModifiedMs: number;
  sizeBytes: number;
}

export interface SyncResult {
  success: boolean;
  completedAtMs: number;
  dataUploaded: number;
  dataDownloaded: number;
  dataDeleted: number;
  codeUploaded: number;
  codeDownloaded: number;
  /** Modules deleted locally because they disappeared from Drive. */
  codeDeleted: number;
  errors: string[];
}

export function emptyResult(): SyncResult {
  return {
    success: false, completedAtMs: 0,
    dataUploaded: 0, dataDownloaded: 0, dataDeleted: 0,
    codeUploaded: 0, codeDownloaded: 0, codeDeleted: 0,
    errors: [],
  };
}
